import { Injectable } from '@angular/core';
import { EMPTY, Observable, from, partition } from 'rxjs';
import { concatMap, map, share } from 'rxjs/operators';
import {
  AbstractWebSocketService,
  WebSocketServiceConfig,
  WebSocketRequestOptions,
  ICommandResponse,
  WebSocketConnectionState,
} from 'types';
import { HWServiceConfig } from './hw-service-config';

export interface SignatureScannerCapabilities {
  'screen-width': number;
  'screen-height': number;
  'max-x': number;
  'max-y': number;
}

export interface SignatureScannerConfiguration {
  image?: string;
  zones?: Array<{
    x: number;
    y: number;
    width: number;
    height: number;
    type: 'signature' | 'stop' | 'clear' | 'user';
    id?: number;
  }>;
}

export interface SignatureScannerDevice {
  id: string;
  model: string;
}

@Injectable()
export class SignatureScannerService extends AbstractWebSocketService {
  public constructor(config: HWServiceConfig) {
    super(new WebSocketServiceConfig(config.getHWServiceUrl(), config.getSignatureScannerServiceProtocol()));
  }

  /** Получить список доступных устройств */
  public listDevices(): Observable<SignatureScannerDevice[]> {
    return this.sendRequest({ command: 'list-devices' }, new WebSocketRequestOptions()).pipe(
      map((r) => (r as ICommandResponse)['devices']),
    );
  }

  /** Устанавливает подключение к выбранному устройству */
  public open(deviceId: string): Observable<void> {
    return this.sendRequest({ command: 'open', id: deviceId }, new WebSocketRequestOptions()).pipe(map((r) => undefined));
  }

  /** Закрывает подключение к текущему устройству */
  public close(): Observable<void> {
    return this.sendRequest({ command: 'close' }, new WebSocketRequestOptions()).pipe(map((r) => undefined));
  }

  public tryClose(): Observable<void> {
    if (this.state === WebSocketConnectionState.Closed || this.state === WebSocketConnectionState.Disconnecting) {
      return EMPTY;
    } else {
      return this.close();
    }
  }

  /** Начать сессию чтения */
  public read(): { result: Observable<Uint8Array>; streamURL: Observable<string> } {
    const source = this.sendRequest({ command: 'read', progress: false }, { expectedJsons: 1, expectedBlobs: 1 }).pipe(share());
    let [result, readingStarted] = partition(source, (x) => x instanceof Blob);

    return {
      result: result.pipe(concatMap((x) => from((x as Blob).arrayBuffer()).pipe(map((b) => new Uint8Array(b))))),
      streamURL: readingStarted.pipe(
        map((x) => this.config.url.replace('ws://', 'http://') + 'sigscanner/stream' + (x as any)['streaming-key']),
      ),
    };
  }

  /** Очистить область ввода подписи */
  public clear(): Observable<void> {
    return this.sendRequest({ command: 'clear', progress: false }, new WebSocketRequestOptions()).pipe(map((r) => undefined));
  }

  /** Закончить сессию чтения */
  public stopReading(): Observable<void> {
    return this.sendRequest({ command: 'stop-reading', progress: false }, new WebSocketRequestOptions()).pipe(map((r) => undefined));
  }

  /** Получение информации о размере экрана и разрешении текущего устройства */
  public getCapabilities(): Observable<SignatureScannerCapabilities> {
    return this.sendRequest({ command: 'get-capabilities' }, new WebSocketRequestOptions()).pipe(
      map((res) => (res as ICommandResponse)['capabilities']),
    );
  }

  /** Задание изображения, выводимого на экране планшета, и активных областей, при активации которых будут выполняться некоторые действия */
  public configure(conf: SignatureScannerConfiguration): Observable<void> {
    return this.sendRequest({ command: 'configure', ...conf }, new WebSocketRequestOptions()).pipe(map((r) => undefined));
  }
}
